import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useMatch, useNavigate, useParams } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Plus } from 'lucide-react';
import { useActiveClinic } from '@/lib/activeClinic';
import {
  APPOINTMENT_TYPES_LIST_PATH,
  appointmentTypeServicePath,
} from '@/lib/appointmentTypeCatalog';
import { useAuth } from '@/lib/auth';
import { useClinicMembers } from '@/hooks/useClinicMembers';
import { useClinicRoles } from '@/hooks/useClinicRoles';
import { filterPractitionerMembers, withStickySelectedMembers } from '@/lib/practitionerRole';
import { useClinic } from '@/hooks/useClinicSettings';
import { useFormsTemplateList, type FormsTemplateSummary } from '@/hooks/useFormsTemplates';
import {
  useReplaceServiceResourceRequirements,
  useResourceGroups,
  useResources,
  useServicePractitioners,
  useServiceResourceRequirements,
  type PractitionerEligibilityMode,
  type ServiceResourceRequirementInput,
} from '@/hooks/useSchedulingResources';
import {
  useCreateService,
  useServicesList,
  useServiceQuestionnaireAutomations,
  useUpdateService,
  type ServiceInput,
} from '@/hooks/useServices';
import {
  linkTreatmentCourseTemplateCatalogService,
  useReplaceTreatmentCourseTemplate,
  useTreatmentCourseTemplate,
  useTreatmentCourseTemplateByCatalogService,
} from '@/hooks/useTreatmentCourseTemplates';
import { useUnsavedPageGuard } from '@/hooks/useUnsavedPageGuard';
import { parsePaymentTiming } from '@/lib/bookingPolicies';
import { parsePractitionerEligibility } from '@/lib/serviceBookingEligibility';
import { DEFAULT_SERVICE_FORM, serviceInputFromRow } from '@/lib/serviceEditorDefaults';
import { clinicVatRatePercent, itemIncludesVat, itemVatRatePercent } from '@/lib/vat';
import {
  SERVICE_DURATION_MAX,
  SERVICE_DURATION_MIN,
  durationMinutesForSave,
  isInvalidServiceDurationError,
} from '@/lib/serviceDuration';
import {
  addTreatmentCourseEditorStep,
  createTreatmentCourseEditorState,
  formatStepDurationSummary,
  formatStepIntervalSummary,
  moveTreatmentCourseEditorStep,
  removeTreatmentCourseEditorStep,
  selectTreatmentCourseEditorItem,
  stepHasValidationError,
  updateTreatmentCourseEditorDraft,
  updateTreatmentCourseEditorStep,
  type TreatmentCourseEditorState,
  type TreatmentCourseStepPanelTab,
} from '@/lib/treatmentCourseEditorState';
import {
  cloneTemplateDraft,
  createEmptyTemplateDraft,
  validateTreatmentCourseTemplateDraft,
} from '@/lib/treatmentCourseTemplatePayload';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Tabs } from '@/components/ui/Tabs';
import { Toggle } from '@/components/ui/Toggle';
import { UnsavedChangesModal } from '@/components/ui/UnsavedChangesModal';
import { FORM_HINT_TYPE, TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';
import { SettingsBackLink, SettingsLoading } from './settingsChrome';
import {
  BasicInformationFields,
  BookingRequirementsFields,
  PricingFields,
  QuestionnairesFields,
} from './ServiceFormModal';
import {
  TreatmentCourseAppointmentFields,
  TreatmentCourseGeneralFields,
  TreatmentCoursePractitionerFields,
  TreatmentCourseQuestionnaireFields,
  TreatmentCourseResourceFields,
} from './treatmentCourseEditorFields';
import { SortableStepItem } from './treatmentCourseStepCard';

const LIST_PATH = APPOINTMENT_TYPES_LIST_PATH;

export function TreatmentCourseEditorPage() {
  const { id } = useParams<{ id: string }>();
  const courseMatch = useMatch('/settings/services/course/:id');
  const serviceMatch = useMatch('/settings/services/:id');
  const isCourseRoute = Boolean(courseMatch);
  const isNew = !id;
  const serviceId = !isNew && !isCourseRoute && serviceMatch ? id : null;
  const templateId = isCourseRoute ? id : null;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeClinicId } = useActiveClinic();
  const { isClinicAdmin, isPlatformAdmin } = useAuth();
  const canEdit = Boolean(activeClinicId && (isPlatformAdmin || isClinicAdmin(activeClinicId)));

  const {
    data: routeTemplate,
    isLoading: routeTemplateLoading,
    error: routeTemplateError,
  } = useTreatmentCourseTemplate(activeClinicId, templateId);
  const {
    data: catalogTemplate,
    isLoading: catalogTemplateLoading,
    error: catalogTemplateError,
  } = useTreatmentCourseTemplateByCatalogService(activeClinicId, serviceId);
  const { data: services, isLoading: servicesLoading } = useServicesList(activeClinicId, {
    includeInactive: true,
  });
  const editingService = serviceId
    ? ((services ?? []).find((svc) => svc.id === serviceId) ?? null)
    : null;
  const template = isCourseRoute ? routeTemplate : catalogTemplate;
  const templateLoading = isCourseRoute
    ? routeTemplateLoading
    : Boolean(serviceId) && catalogTemplateLoading;
  const templateError = isCourseRoute ? routeTemplateError : catalogTemplateError;

  const replaceTemplate = useReplaceTreatmentCourseTemplate(activeClinicId);
  const createService = useCreateService(activeClinicId);
  const updateService = useUpdateService(activeClinicId);
  const replaceRequirements = useReplaceServiceResourceRequirements(activeClinicId);
  const { data: clinic } = useClinic(activeClinicId);
  const clinicVatRate = clinicVatRatePercent(clinic);
  const { data: resourceGroups } = useResourceGroups(activeClinicId);
  const { data: resources } = useResources(activeClinicId);
  const { data: members } = useClinicMembers(activeClinicId);
  const { data: roles, isSuccess: rolesReady } = useClinicRoles(activeClinicId);
  const { data: allRequirements, isLoading: requirementsLoading } =
    useServiceResourceRequirements(activeClinicId);
  const { data: allServicePractitioners, isLoading: practitionersLoading } =
    useServicePractitioners(activeClinicId);
  const { data: allAutomations, isLoading: automationsLoading } =
    useServiceQuestionnaireAutomations(activeClinicId);
  const { data: templateLibrary, isLoading: templatesLoading } = useFormsTemplateList(
    activeClinicId ?? undefined,
  );

  const [state, setState] = useState<TreatmentCourseEditorState>(() =>
    createTreatmentCourseEditorState(createEmptyTemplateDraft()),
  );
  const [serviceForm, setServiceForm] = useState<ServiceInput>(() => ({
    ...DEFAULT_SERVICE_FORM,
    vat_rate_percent: clinicVatRate,
  }));
  const [requirements, setRequirements] = useState<ServiceResourceRequirementInput[]>([]);
  const [practitionerEligibility, setPractitionerEligibility] =
    useState<PractitionerEligibilityMode>('all');
  const [selectedPractitionerIds, setSelectedPractitionerIds] = useState<string[]>([]);
  const checklistMembers = useMemo(() => {
    const all = members ?? [];
    if (!rolesReady) return all;
    const pinned = [
      ...selectedPractitionerIds,
      ...state.draft.steps.flatMap((step) => step.practitioner_ids),
    ];
    return withStickySelectedMembers(filterPractitionerMembers(all, roles ?? []), all, pinned);
  }, [members, roles, rolesReady, selectedPractitionerIds, state.draft.steps]);
  const [baseline, setBaseline] = useState('');
  const [stepTab, setStepTab] = useState<TreatmentCourseStepPanelTab>('appointment');
  const initializedKey = useRef<string | null>(null);

  useEffect(() => {
    if (isCourseRoute && routeTemplate?.catalog_service_id) {
      navigate(appointmentTypeServicePath(routeTemplate.catalog_service_id), { replace: true });
    }
  }, [isCourseRoute, navigate, routeTemplate?.catalog_service_id]);

  useEffect(() => {
    if (isNew) {
      if (initializedKey.current === 'new') return;
      const nextDraft = createEmptyTemplateDraft();
      const nextForm = { ...DEFAULT_SERVICE_FORM, vat_rate_percent: clinicVatRate };
      initializedKey.current = 'new';
      setState(
        createTreatmentCourseEditorState(nextDraft, {
          kind: 'step',
          clientId: nextDraft.steps[0]?.clientId ?? '',
        }),
      );
      setServiceForm(nextForm);
      setRequirements([]);
      setPractitionerEligibility('all');
      setSelectedPractitionerIds([]);
      setBaseline(serializeEditor(nextDraft, nextForm, [], 'all', []));
      setStepTab('questionnaires');
      return;
    }
    if (isCourseRoute) {
      if (!template?.id || initializedKey.current === `course:${template.id}`) return;
      const nextDraft = cloneTemplateDraft(template);
      initializedKey.current = `course:${template.id}`;
      setState(createTreatmentCourseEditorState(nextDraft));
      setBaseline(serializeEditor(nextDraft, DEFAULT_SERVICE_FORM, [], 'all', []));
      setStepTab('appointment');
      return;
    }
    if (
      !serviceId ||
      servicesLoading ||
      catalogTemplateLoading ||
      requirementsLoading ||
      practitionersLoading ||
      automationsLoading
    ) {
      return;
    }
    if (!editingService) return;
    const initKey = `service:${editingService.id}:${template?.id ?? 'none'}`;
    if (initializedKey.current === initKey) return;
    const serviceAutomations = (allAutomations ?? []).filter(
      (row) => row.service_id === editingService.id,
    );
    const nextForm = serviceInputFromRow(editingService, serviceAutomations);
    const nextDraft = template
      ? cloneTemplateDraft({ ...template, catalog_service_id: editingService.id })
      : draftFromService(editingService);
    initializedKey.current = initKey;
    const nextRequirements = (allRequirements ?? [])
      .filter((req) => req.service_id === editingService.id)
      .map((req) => ({
        resource_group_id: req.resource_group_id,
        quantity: req.quantity,
        eligibility_mode: req.eligibility_mode,
        eligible_resource_ids: req.eligible_resource_ids,
      }));
    setServiceForm(nextForm);
    setRequirements(nextRequirements);
    const eligibility = parsePractitionerEligibility(editingService.practitioner_eligibility);
    const practitionerIds = (allServicePractitioners ?? [])
      .filter((row) => row.service_id === editingService.id)
      .map((row) => row.practitioner_id);
    setPractitionerEligibility(eligibility);
    setSelectedPractitionerIds(practitionerIds);
    setState(
      createTreatmentCourseEditorState(
        nextDraft,
        nextDraft.steps.length > 1
          ? { kind: 'general' }
          : { kind: 'step', clientId: nextDraft.steps[0]?.clientId ?? '' },
      ),
    );
    setBaseline(
      serializeEditor(nextDraft, nextForm, nextRequirements, eligibility, practitionerIds),
    );
    setStepTab(nextDraft.steps.length > 1 ? 'appointment' : 'questionnaires');
  }, [
    allAutomations,
    allRequirements,
    allServicePractitioners,
    automationsLoading,
    catalogTemplateLoading,
    clinicVatRate,
    editingService,
    isCourseRoute,
    isNew,
    practitionersLoading,
    requirementsLoading,
    serviceId,
    servicesLoading,
    template,
  ]);

  const isMultiStep = state.draft.steps.length > 1;
  const isServiceBacked = isNew || Boolean(serviceId);
  const currentSnapshot = serializeEditor(
    state.draft,
    serviceForm,
    requirements,
    practitionerEligibility,
    selectedPractitionerIds,
  );
  const isDirty = Boolean(baseline) && currentSnapshot !== baseline;
  const unsaved = useUnsavedPageGuard(isDirty && canEdit);
  const bookableServices = (services ?? []).filter((svc) => !svc.is_blocker);
  const allTemplates = [...(templateLibrary?.platform ?? []), ...(templateLibrary?.clinic ?? [])];
  const title =
    isServiceBacked && !isMultiStep
      ? serviceForm.name.trim() || t('services.new_service')
      : state.draft.name.trim() ||
        (isServiceBacked ? t('services.new_service') : t('treatment_courses.new_course'));
  const selected = state.selected;
  const selectedStep =
    selected.kind === 'step'
      ? state.draft.steps.find((step) => step.clientId === selected.clientId)
      : undefined;
  const firstStep = state.draft.steps[0];
  const saving =
    replaceTemplate.isPending ||
    createService.isPending ||
    updateService.isPending ||
    replaceRequirements.isPending;

  function leaveToList() {
    navigate(LIST_PATH);
  }

  function applyActive(is_active: boolean) {
    setServiceForm((current) => ({ ...current, is_active }));
    setState((current) =>
      updateTreatmentCourseEditorDraft(current, { ...current.draft, is_active }),
    );
  }

  function onAddStep() {
    setState((current) => {
      const withService =
        isServiceBacked && current.draft.steps[0] && !current.draft.steps[0].service_id && serviceId
          ? updateTreatmentCourseEditorStep(current, current.draft.steps[0].clientId, {
              ...current.draft.steps[0],
              service_id: serviceId,
            })
          : current;
      const named =
        isServiceBacked && !withService.draft.name.trim()
          ? updateTreatmentCourseEditorDraft(withService, {
              ...withService.draft,
              name: serviceForm.name,
              description: serviceForm.description ?? '',
              color: serviceForm.color || withService.draft.color,
              is_active: serviceForm.is_active ?? true,
            })
          : withService;
      return addTreatmentCourseEditorStep(named);
    });
    setStepTab('appointment');
  }

  async function persistTemplate(draft: typeof state.draft, catalogServiceId: string | null) {
    const toSave = {
      ...draft,
      catalog_service_id: catalogServiceId,
    };
    const saved = await replaceTemplate.mutateAsync(toSave);
    if (saved.id && catalogServiceId) {
      await linkTreatmentCourseTemplateCatalogService(
        activeClinicId as string,
        saved.id,
        catalogServiceId,
      );
    }
    return saved;
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!canEdit) return;

    if (isServiceBacked) {
      const name = (isMultiStep ? state.draft.name : serviceForm.name).trim();
      if (!name) {
        toast.error(t('treatment_courses.validation_name_required'));
        if (isMultiStep) {
          setState((current) => selectTreatmentCourseEditorItem(current, { kind: 'general' }));
        }
        return;
      }
      const duration_minutes = durationMinutesForSave(
        event.currentTarget,
        serviceForm.duration_minutes,
      );
      if (duration_minutes == null) {
        toast.error(
          t('services.field_duration_invalid', {
            min: SERVICE_DURATION_MIN,
            max: SERVICE_DURATION_MAX,
          }),
        );
        return;
      }
      const nextDraft = {
        ...state.draft,
        name,
        description: isMultiStep ? state.draft.description : (serviceForm.description ?? ''),
        color: isMultiStep ? state.draft.color : serviceForm.color || state.draft.color,
        is_active: isMultiStep ? state.draft.is_active : Boolean(serviceForm.is_active),
      };
      if (isMultiStep) {
        const validation = validateTreatmentCourseTemplateDraft({
          ...nextDraft,
          steps: nextDraft.steps.map((step, index) =>
            index === 0 && !step.service_id && serviceId
              ? { ...step, service_id: serviceId }
              : step,
          ),
        });
        if (validation) {
          toast.error(t(`treatment_courses.validation_${validation}`));
          if (validation === 'service_required') {
            const firstInvalid = nextDraft.steps.find((step, index) =>
              index === 0 && serviceId ? false : stepHasValidationError(step),
            );
            if (firstInvalid) {
              setState((current) =>
                selectTreatmentCourseEditorItem(current, {
                  kind: 'step',
                  clientId: firstInvalid.clientId,
                }),
              );
              setStepTab('appointment');
            }
          }
          return;
        }
      }
      try {
        const payload = {
          ...serviceForm,
          name,
          duration_minutes,
          description: nextDraft.description,
          color: nextDraft.color,
          is_active: nextDraft.is_active,
          vat_rate_percent: itemVatRatePercent(
            itemIncludesVat(serviceForm.vat_rate_percent),
            clinicVatRate,
          ),
        };
        const savedService = serviceId
          ? await updateService.mutateAsync({ id: serviceId, ...payload })
          : await createService.mutateAsync(payload);
        if (!payload.is_blocker) {
          await replaceRequirements.mutateAsync({
            service_id: savedService.id,
            practitioner_eligibility: practitionerEligibility,
            practitioner_ids: practitionerEligibility === 'selected' ? selectedPractitionerIds : [],
            requirements,
          });
        }
        const steps = nextDraft.steps.map((step, index) =>
          index === 0 && !step.service_id ? { ...step, service_id: savedService.id } : step,
        );
        const savedTemplate = await persistTemplate(
          { ...nextDraft, steps, catalog_service_id: savedService.id },
          savedService.id,
        );
        toast.success(serviceId ? t('services.toast_updated') : t('services.toast_created'));
        const cloned = cloneTemplateDraft({
          ...savedTemplate,
          catalog_service_id: savedService.id,
        });
        initializedKey.current = `service:${savedService.id}:${cloned.id ?? 'none'}`;
        setServiceForm({ ...payload, name });
        setState((current) => updateTreatmentCourseEditorDraft(current, cloned));
        setBaseline(
          serializeEditor(
            cloned,
            { ...payload, name },
            requirements,
            practitionerEligibility,
            selectedPractitionerIds,
          ),
        );
        if (!serviceId) {
          navigate(appointmentTypeServicePath(savedService.id), { replace: true });
        }
      } catch (err) {
        console.error('appointment type save failed', err);
        const message = err instanceof Error ? err.message : '';
        if (
          message.toLowerCase().includes('unique') ||
          message.toLowerCase().includes('duplicate')
        ) {
          toast.error(t('treatment_courses.toast_duplicate_name'));
          return;
        }
        if (isInvalidServiceDurationError(err)) {
          toast.error(
            t('services.field_duration_invalid', {
              min: SERVICE_DURATION_MIN,
              max: SERVICE_DURATION_MAX,
            }),
          );
          return;
        }
        toast.error(t('services.toast_error'));
      }
      return;
    }

    const validation = validateTreatmentCourseTemplateDraft(state.draft);
    if (validation) {
      toast.error(t(`treatment_courses.validation_${validation}`));
      if (validation === 'name_required') {
        setState((current) => selectTreatmentCourseEditorItem(current, { kind: 'general' }));
      } else if (validation === 'service_required') {
        const firstInvalid = state.draft.steps.find((step) => stepHasValidationError(step));
        if (firstInvalid) {
          setState((current) =>
            selectTreatmentCourseEditorItem(current, {
              kind: 'step',
              clientId: firstInvalid.clientId,
            }),
          );
          setStepTab('appointment');
        }
      }
      return;
    }
    try {
      const saved = await persistTemplate(state.draft, state.draft.catalog_service_id ?? null);
      toast.success(t('treatment_courses.toast_updated'));
      const nextDraft = cloneTemplateDraft(saved);
      initializedKey.current = `course:${saved.id}`;
      setState((current) => updateTreatmentCourseEditorDraft(current, nextDraft));
      setBaseline(serializeEditor(nextDraft, DEFAULT_SERVICE_FORM, [], 'all', []));
    } catch (err) {
      console.error('treatment course template save failed', err);
      const message = err instanceof Error ? err.message : '';
      if (message.toLowerCase().includes('unique') || message.toLowerCase().includes('duplicate')) {
        toast.error(t('treatment_courses.toast_duplicate_name'));
        return;
      }
      toast.error(t('treatment_courses.toast_error'));
    }
  }

  if (isNew && !isCourseRoute && !serviceId) {
    // create page — no load
  } else if (isCourseRoute && routeTemplateLoading) {
    return <SettingsLoading />;
  } else if (isCourseRoute && routeTemplateError) {
    return <ErrorAlert message={t('treatment_courses.load_error')} />;
  } else if (isCourseRoute && !template) {
    return <ErrorAlert message={t('treatment_courses.not_found')} />;
  } else if (
    serviceId &&
    (servicesLoading ||
      templateLoading ||
      requirementsLoading ||
      practitionersLoading ||
      automationsLoading)
  ) {
    return <SettingsLoading />;
  } else if (serviceId && templateError) {
    return <ErrorAlert message={t('services.load_error')} />;
  } else if (serviceId && !editingService) {
    return <ErrorAlert message={t('treatment_courses.not_found')} />;
  }

  const showStepList = isMultiStep;

  return (
    <div className="flex min-w-0 flex-col" data-testid="treatment-course-editor">
      <SettingsBackLink to={LIST_PATH} label={t('services.back_to_list')} />

      <form onSubmit={onSubmit} className="flex min-w-0 flex-col">
        <div className="bg-ds-bg/95 sticky top-0 z-20 -mx-s1 border-b border-ds-line px-s1 py-s3 backdrop-blur">
          <div className="flex min-w-0 flex-wrap items-center justify-between gap-s3">
            <h2 className={cn(TYPE.heading, 'min-w-0 truncate text-ds-text')}>{title}</h2>
            <div className="flex flex-wrap items-center gap-s3">
              <Toggle
                checked={
                  isServiceBacked && !isMultiStep
                    ? Boolean(serviceForm.is_active)
                    : state.draft.is_active
                }
                onCheckedChange={applyActive}
                label={t('treatment_courses.field_is_active')}
                disabled={!canEdit}
              />
              <Button
                type="button"
                variant="secondary"
                onClick={() => unsaved.requestLeave(leaveToList)}
              >
                {t('common.cancel')}
              </Button>
              {canEdit ? (
                <Button type="submit" loading={saving} loadingText={t('common.saving')}>
                  {t('common.save')}
                </Button>
              ) : null}
            </div>
          </div>
        </div>

        {showStepList ? (
          <div className="mt-s5 grid grid-cols-1 items-start gap-s4 nav:grid-cols-[minmax(17rem,22rem)_minmax(0,1fr)]">
            <Card className="min-w-0" data-testid="treatment-course-step-list">
              <StepList
                state={state}
                services={bookableServices}
                canEdit={canEdit}
                onSelectGeneral={() =>
                  setState((current) =>
                    selectTreatmentCourseEditorItem(current, { kind: 'general' }),
                  )
                }
                onSelectStep={(clientId) => {
                  setState((current) =>
                    selectTreatmentCourseEditorItem(current, { kind: 'step', clientId }),
                  );
                  setStepTab('appointment');
                }}
                onAdd={onAddStep}
                onMove={(from, to) =>
                  setState((current) => moveTreatmentCourseEditorStep(current, from, to))
                }
                onRemove={(clientId) =>
                  setState((current) => removeTreatmentCourseEditorStep(current, clientId))
                }
              />
            </Card>

            <Card className="min-w-0" data-testid="treatment-course-editor-panel">
              {state.selected.kind === 'general' ? (
                <TreatmentCourseGeneralFields
                  draft={state.draft}
                  onChange={(draft) =>
                    setState((current) => updateTreatmentCourseEditorDraft(current, draft))
                  }
                  disabled={!canEdit}
                />
              ) : selectedStep ? (
                <StepEditorPanel
                  step={selectedStep}
                  index={state.draft.steps.findIndex(
                    (step) => step.clientId === selectedStep.clientId,
                  )}
                  stepCount={state.draft.steps.length}
                  tab={stepTab}
                  onTabChange={setStepTab}
                  services={bookableServices}
                  members={checklistMembers}
                  resourceGroups={resourceGroups ?? []}
                  resources={resources ?? []}
                  templates={allTemplates}
                  templatesLoading={templatesLoading}
                  disabled={!canEdit}
                  onChange={(next) =>
                    setState((current) =>
                      updateTreatmentCourseEditorStep(current, selectedStep.clientId, next),
                    )
                  }
                />
              ) : null}
            </Card>
          </div>
        ) : (
          <Card className="mt-s5 min-w-0" data-testid="treatment-course-editor-panel">
            {isServiceBacked && firstStep ? (
              <SingleTypePanel
                form={serviceForm}
                onFormChange={setServiceForm}
                requirements={requirements}
                onRequirementsChange={setRequirements}
                practitionerEligibility={practitionerEligibility}
                onPractitionerEligibilityChange={setPractitionerEligibility}
                selectedPractitionerIds={selectedPractitionerIds}
                onSelectedPractitionerIdsChange={setSelectedPractitionerIds}
                step={firstStep}
                members={checklistMembers}
                resourceGroups={resourceGroups ?? []}
                resources={resources ?? []}
                templates={allTemplates}
                templatesLoading={templatesLoading}
                clinicVatRate={clinicVatRate}
                clinicPaymentTiming={parsePaymentTiming(clinic?.payment_timing)}
                disabled={!canEdit}
                canAddStep={canEdit && !serviceForm.is_blocker}
                onStepChange={(next) =>
                  setState((current) =>
                    updateTreatmentCourseEditorStep(current, firstStep.clientId, next),
                  )
                }
                onAddStep={onAddStep}
              />
            ) : selectedStep ? (
              <StepEditorPanel
                step={selectedStep}
                index={0}
                stepCount={1}
                tab={stepTab}
                onTabChange={setStepTab}
                services={bookableServices}
                members={checklistMembers}
                resourceGroups={resourceGroups ?? []}
                resources={resources ?? []}
                templates={allTemplates}
                templatesLoading={templatesLoading}
                disabled={!canEdit}
                onChange={(next) =>
                  setState((current) =>
                    updateTreatmentCourseEditorStep(current, selectedStep.clientId, next),
                  )
                }
              />
            ) : null}
          </Card>
        )}
      </form>

      <UnsavedChangesModal
        open={unsaved.open}
        onContinue={unsaved.continueEditing}
        onDiscard={unsaved.discardAndLeave}
      />
    </div>
  );
}

function serializeEditor(
  draft: TreatmentCourseEditorState['draft'],
  form: ServiceInput,
  requirements: ServiceResourceRequirementInput[],
  practitionerEligibility: PractitionerEligibilityMode,
  selectedPractitionerIds: string[],
): string {
  return JSON.stringify({
    draft,
    form,
    requirements,
    practitionerEligibility,
    selectedPractitionerIds,
  });
}

function draftFromService(service: {
  id: string;
  name: string;
  description: string | null;
  color: string;
  is_active: boolean;
}) {
  const draft = createEmptyTemplateDraft();
  draft.catalog_service_id = service.id;
  draft.name = service.name;
  draft.description = service.description ?? '';
  draft.color = service.color || draft.color;
  draft.is_active = service.is_active;
  if (draft.steps[0]) draft.steps[0].service_id = service.id;
  return draft;
}

function SingleTypePanel({
  form,
  onFormChange,
  requirements,
  onRequirementsChange,
  practitionerEligibility,
  onPractitionerEligibilityChange,
  selectedPractitionerIds,
  onSelectedPractitionerIdsChange,
  step,
  members,
  resourceGroups,
  resources,
  templates,
  templatesLoading,
  clinicVatRate,
  clinicPaymentTiming,
  disabled,
  canAddStep,
  onStepChange,
  onAddStep,
}: {
  form: ServiceInput;
  onFormChange: (next: ServiceInput) => void;
  requirements: ServiceResourceRequirementInput[];
  onRequirementsChange: (next: ServiceResourceRequirementInput[]) => void;
  practitionerEligibility: PractitionerEligibilityMode;
  onPractitionerEligibilityChange: (next: PractitionerEligibilityMode) => void;
  selectedPractitionerIds: string[];
  onSelectedPractitionerIdsChange: (next: string[]) => void;
  step: TreatmentCourseEditorState['draft']['steps'][number];
  members: Array<{ user_id: string; name: string }>;
  resourceGroups: Array<{ id: string; name: string; is_active: boolean }>;
  resources: Array<{
    id: string;
    name: string;
    resource_group_id: string;
    is_active: boolean;
  }>;
  templates: FormsTemplateSummary[];
  templatesLoading: boolean;
  clinicVatRate: number;
  clinicPaymentTiming: ReturnType<typeof parsePaymentTiming>;
  disabled: boolean;
  canAddStep: boolean;
  onStepChange: (next: TreatmentCourseEditorState['draft']['steps'][number]) => void;
  onAddStep: () => void;
}) {
  const { t } = useTranslation();
  return (
    <fieldset disabled={disabled} className="space-y-s5">
      <section className="space-y-4">
        <h3 className={cn(TYPE.section, 'text-ds-text')}>{t('services.section_basic')}</h3>
        <BasicInformationFields form={form} onChange={onFormChange} />
      </section>
      <section className="space-y-4">
        <h3 className={cn(TYPE.section, 'text-ds-text')}>{t('services.section_pricing')}</h3>
        <PricingFields
          form={form}
          onChange={onFormChange}
          clinicVatRate={clinicVatRate}
          clinicPaymentTiming={clinicPaymentTiming}
        />
      </section>
      <section className="space-y-4">
        <h3 className={cn(TYPE.section, 'text-ds-text')}>{t('services.booking_requirements')}</h3>
        <p className={FORM_HINT_TYPE}>{t('services.booking_requirements_hint')}</p>
        <BookingRequirementsFields
          requirements={requirements}
          onRequirementsChange={onRequirementsChange}
          practitionerEligibility={practitionerEligibility}
          onPractitionerEligibilityChange={onPractitionerEligibilityChange}
          selectedPractitionerIds={selectedPractitionerIds}
          onSelectedPractitionerIdsChange={onSelectedPractitionerIdsChange}
          members={members}
          resourceGroups={resourceGroups}
          resources={resources}
        />
      </section>
      <section className="space-y-4">
        <TreatmentCourseQuestionnaireFields
          step={step}
          stepIndex={0}
          stepTitle={form.name.trim() || t('services.new_service')}
          templates={templates}
          templatesLoading={templatesLoading}
          onChange={onStepChange}
          disabled={disabled}
        />
        <div className="space-y-3 border-t border-ds-line pt-s4">
          <h3 className={cn(TYPE.section, 'text-ds-text')}>
            {t('services.section_questionnaires')}
          </h3>
          <p className={FORM_HINT_TYPE}>{t('services.section_questionnaires_hint')}</p>
          <QuestionnairesFields
            form={form}
            onChange={onFormChange}
            templates={templates}
            templatesLoading={templatesLoading}
          />
        </div>
      </section>
      {canAddStep ? (
        <Button type="button" variant="secondary" onClick={onAddStep}>
          <Plus className="mr-1 h-4 w-4" />
          {t('treatment_courses.add_step')}
        </Button>
      ) : null}
    </fieldset>
  );
}

function StepList({
  state,
  services,
  canEdit,
  onSelectGeneral,
  onSelectStep,
  onAdd,
  onMove,
  onRemove,
}: {
  state: TreatmentCourseEditorState;
  services: Array<{ id: string; name: string; duration_minutes: number }>;
  canEdit: boolean;
  onSelectGeneral: () => void;
  onSelectStep: (clientId: string) => void;
  onAdd: () => void;
  onMove: (fromIndex: number, toIndex: number) => void;
  onRemove: (clientId: string) => void;
}) {
  const { t } = useTranslation();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const stepIds = state.draft.steps.map((step) => step.clientId);

  function onDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const fromIndex = stepIds.indexOf(String(active.id));
    const toIndex = stepIds.indexOf(String(over.id));
    if (fromIndex < 0 || toIndex < 0) return;
    onMove(fromIndex, toIndex);
  }

  return (
    <div className="space-y-s3">
      <button
        type="button"
        data-testid="treatment-course-selection-general"
        onClick={onSelectGeneral}
        className={cn(
          TYPE.control,
          'flex w-full items-start rounded-ctl px-s3 py-s3 text-left font-medium transition-colors',
          state.selected.kind === 'general'
            ? 'bg-ds-teal-tint font-semibold text-ds-teal'
            : 'text-ds-text hover:bg-ds-neutral-tint',
        )}
      >
        {t('treatment_courses.section_general')}
      </button>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={stepIds} strategy={verticalListSortingStrategy}>
          <ol className="relative space-y-s2">
            {state.draft.steps.map((step, index) => {
              const service = services.find((svc) => svc.id === step.service_id);
              return (
                <SortableStepItem
                  key={step.clientId}
                  index={index}
                  stepCount={state.draft.steps.length}
                  selected={
                    state.selected.kind === 'step' && state.selected.clientId === step.clientId
                  }
                  title={t('treatment_courses.step_n', { n: index + 1 })}
                  serviceName={service?.name ?? t('treatment_courses.step_no_service')}
                  duration={formatStepDurationSummary(step, service?.duration_minutes, t)}
                  interval={formatStepIntervalSummary(step, index, t)}
                  allowedCount={step.allowed_forms_template_ids.length}
                  hasError={stepHasValidationError(step)}
                  canEdit={canEdit}
                  clientId={step.clientId}
                  onSelect={() => onSelectStep(step.clientId)}
                  onMoveUp={() => onMove(index, index - 1)}
                  onMoveDown={() => onMove(index, index + 1)}
                  onRemove={() => onRemove(step.clientId)}
                />
              );
            })}
          </ol>
        </SortableContext>
      </DndContext>

      {canEdit ? (
        <Button type="button" variant="secondary" size="sm" onClick={onAdd}>
          <Plus className="mr-1 h-4 w-4" />
          {t('treatment_courses.add_step')}
        </Button>
      ) : null}
      <p className="sk stp-sum num" data-testid="treatment-course-allowed-summary">
        <Trans
          i18nKey="treatment_courses.allowed_list_summary"
          values={{
            total: state.draft.steps.reduce(
              (sum, step) => sum + step.allowed_forms_template_ids.length,
              0,
            ),
            steps: state.draft.steps.filter((step) => step.allowed_forms_template_ids.length > 0)
              .length,
            of: state.draft.steps.length,
          }}
          components={{ b: <b /> }}
        />
      </p>
    </div>
  );
}

function StepEditorPanel({
  step,
  index,
  stepCount,
  tab,
  onTabChange,
  services,
  members,
  resourceGroups,
  resources,
  templates,
  templatesLoading,
  disabled,
  onChange,
}: {
  step: NonNullable<TreatmentCourseEditorState['draft']['steps'][number]>;
  index: number;
  stepCount: number;
  tab: TreatmentCourseStepPanelTab;
  onTabChange: (tab: TreatmentCourseStepPanelTab) => void;
  services: Array<{ id: string; name: string; duration_minutes: number; is_active: boolean }>;
  members: Array<{ user_id: string; name: string }>;
  resourceGroups: Array<{ id: string; name: string; is_active: boolean }>;
  resources: Array<{
    id: string;
    name: string;
    resource_group_id: string;
    is_active: boolean;
  }>;
  templates: FormsTemplateSummary[];
  templatesLoading: boolean;
  disabled: boolean;
  onChange: (next: TreatmentCourseEditorState['draft']['steps'][number]) => void;
}) {
  const { t } = useTranslation();
  const stepTitle =
    step.title.trim() ||
    services.find((service) => service.id === step.service_id)?.name ||
    t('treatment_courses.step_no_service');
  const showStepHeading = stepCount > 1;

  function renderTab(current: TreatmentCourseStepPanelTab) {
    switch (current) {
      case 'appointment':
        return (
          <TreatmentCourseAppointmentFields
            index={index}
            step={step}
            services={services}
            onChange={onChange}
            disabled={disabled}
          />
        );
      case 'practitioners':
        return (
          <TreatmentCoursePractitionerFields
            step={step}
            members={members}
            onChange={onChange}
            disabled={disabled}
          />
        );
      case 'resources':
        return (
          <TreatmentCourseResourceFields
            step={step}
            resourceGroups={resourceGroups}
            resources={resources}
            onChange={onChange}
            disabled={disabled}
          />
        );
      case 'questionnaires':
        return (
          <TreatmentCourseQuestionnaireFields
            step={step}
            stepIndex={index}
            stepTitle={stepTitle}
            templates={templates}
            templatesLoading={templatesLoading}
            onChange={onChange}
            disabled={disabled}
          />
        );
      default: {
        const _exhaustive: never = current;
        return _exhaustive;
      }
    }
  }

  return (
    <div>
      {showStepHeading ? (
        <p
          className={cn(TYPE.body, 'mb-s3 font-semibold text-ds-text')}
          data-testid="treatment-course-step-heading"
        >
          {t('treatment_courses.step_of_total', { n: index + 1, total: stepCount })}
          {' · '}
          {stepTitle}
        </p>
      ) : null}
      <Tabs
        ariaLabel={t('treatment_courses.step_n', { n: index + 1 })}
        value={tab}
        onChange={(next) => onTabChange(next as TreatmentCourseStepPanelTab)}
        overflowLabel={(count) => t('design.more_tabs', { count })}
        items={[
          { id: 'appointment', label: t('treatment_courses.tab_appointment') },
          { id: 'practitioners', label: t('treatment_courses.tab_practitioners') },
          { id: 'resources', label: t('treatment_courses.tab_resources') },
          {
            id: 'questionnaires',
            label: t('treatment_courses.tab_questionnaires'),
            count: step.allowed_forms_template_ids.length || undefined,
          },
        ]}
      />
      <div className="mt-s4" role="tabpanel">
        {renderTab(tab)}
      </div>
    </div>
  );
}
